import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {useCioAutocomplete} from '@constructor-io/constructorio-ui-autocomplete'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import SearchIcon from '@/assets/icons/search-icon'
import CloseIcon from '@/assets/icons/close-black'
import {cn} from '@/lib/utils'
import useMediaQuery from 'hec/hooks/useMediaQuery'
import {useAppOrigin} from '~/app/hooks/use-app-origin'

const SearchComponent = ({attributes}) => {
    const {className, ...restAttributes} = attributes
    const isTablet = useMediaQuery('lg')
    const config = getConfig()
    const args = {
        cioJsClientOptions: {
            serviceUrl: `${useAppOrigin()}/api/constructor/`
        },
        apiKey: config.app.constructor.apiKey,
        placeholder: 'Search Eligible Products',
        onSubmit: (submitEvent) => {
            window.location.href = submitEvent?.query && `/search?q=${submitEvent?.query}`
        },
        sections: [
            {
                identifier: 'Search Suggestions',
                displayName: 'Trending Searches',
                numResults: 9
            },
            {
                identifier: 'Products',
                displayName: 'Products',
                numResults: 3
            },
            {
                identifier: 'Articles',
                displayName: 'Articles',
                numResults: 3
            }
        ]
    }
    const {
        isOpen,
        sections,
        getFormProps,
        getLabelProps,
        getInputProps,
        getMenuProps,
        getItemProps,
        autocompleteClassName
    } = useCioAutocomplete(args)

    const containsArticles = useMemo(() => {
        const articleData = sections.find((section) => section.identifier === 'Articles')
        return articleData?.data.length > 0
    }, [sections])

    return (
        <div className={cn(`relative w-full`, className)} {...restAttributes}>
            <form {...getFormProps()} className="my-auto w-full">
                <label {...getLabelProps()} className="inline-block w-full">
                    <input
                        {...getInputProps()}
                        className="h-auto text-[.875rem] text-emphasis pt-[.9375rem] font-normal
            pr-[2.4375rem] pb-[.8125rem] pl-[.9375rem] border-[.063rem] border-solid border-input rounded w-full"
                    />
                </label>
                <button className="ml-[-1.875rem] align-middle" type="submit">
                    <SearchIcon
                        height="20"
                        width="20"
                        fill="currentColor"
                        className="text-blue-03"
                        data-testid="search-SearchIcon"
                    />
                </button>
                {isOpen && (
                    <button className="ml-[-2.188rem] align-middle">
                        <CloseIcon
                            height="13"
                            width="13"
                            fill="currentColor"
                            className="text-blue-03"
                            strokeWidth="3"
                            data-testid="search-CloseIcon"
                        />
                    </button>
                )}
            </form>
            <div
                {...getMenuProps()}
                className={cn(
                    autocompleteClassName,
                    'absolute top-full left-0 w-full max-h-100 xs:max-h-[77dvh] overflow-y-auto bg-white shadow-lg rounded border border-gray-200 z-50',
                    {['lg:w-[150%]']: containsArticles},
                    {['lg:w-[120%]']: !containsArticles},
                    {hidden: !isOpen}
                )}
            >
                {isOpen && (
                    <div className="flex flex-col lg:flex-row py-6">
                        {sections?.map((section, index) => (
                            <div
                                key={section.indexSectionName}
                                className={cn(
                                    section.data.length === 0 && 'hidden',
                                    'pl-[1.25rem] pr-[1.25rem] first:max-w-[13.125rem] odd:flex-1 even:flex-2',
                                    section.identifier === 'Articles' &&
                                        !isTablet &&
                                        `search-articles-border box-decoration-slice border-solid border-l-4`,
                                    section.indexSectionName
                                )}
                            >
                                <div className="cio-section">
                                    <h5 className="cio-section-name  uppercase text-accent font-medium tracking-[.04375rem] text-[.875rem] grotesque mt-[0.938rem] mb-[0.938rem]">
                                        {index !== 0 &&
                                            (section?.displayName || section.indexSectionName)}
                                    </h5>
                                    <div className="cio-items min-w-[10rem] text-[0.875rem] font-light xs:leading-tight">
                                        {section?.data?.map((item) => (
                                            <a
                                                {...getItemProps(item)}
                                                key={item?.id}
                                                href={
                                                    item.data.url ||
                                                    `/search?q=${item.value.split(' ').join('+')}`
                                                }
                                            >
                                                <div className="flex mb-[.9375rem] items-center">
                                                    {item.data?.image_url && (
                                                        <img
                                                            className="w-[3.75rem] h-[3.75rem]"
                                                            src={item.data?.image_url}
                                                            alt={item?.value}
                                                            data-testid="cio-img"
                                                        />
                                                    )}
                                                    {item.groupName ? (
                                                        <p className="cio-term-in-group inline-block">
                                                            in {item.groupName}
                                                        </p>
                                                    ) : (
                                                        <p
                                                            className={cn(
                                                                'inline-block text-black',
                                                                {
                                                                    'pl-[.9375rem] font-medium':
                                                                        item.section === 'Products'
                                                                }
                                                            )}
                                                        >
                                                            {item.value}
                                                        </p>
                                                    )}
                                                </div>
                                            </a>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

SearchComponent.propTypes = {
    attributes: PropTypes.object.isRequired
}

export const SearchDefinition = {
    component: SearchComponent,
    name: 'Search',
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: false
    }
}
